import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { fetchIconAction } from '../../../reducers/iconReducer'
import { fetchServices, serviceTwoSelector, statusSelector, updateServicesAction } from '../../../reducers/servicesReducer'
import { AppDispatch } from '../../../store'
import ServiceCard from '../components/ServiceCard'
import ServicesLayout from '../ServicesLayout'
import { KTSVG } from '../../../../_metronic/helpers'
import IconLoading from '../../icons/components/IconLoading'
import ServiceLoading from '../components/ServiceLoading'
import { BsGrid3X3GapFill, BsListUl } from 'react-icons/bs'
import MUITable from '../../../modules/common/MUITable'
import { useServiceTableConfig } from '../components/ServiceTable'
import { Modal, Box } from '@mui/material'
import { serviceOneSelector } from '../../../reducers/servicesReducer'
import { updateServiceParentAction } from '../../../reducers/servicesReducer'
import TableLoading from '../../../../_metronic/layout/components/Common/TableSkeletonLoading'

interface Service {
  id: number;
  name: string;
  icon: string;
  isActive: number;
  isAdmin: number;
  isMemberDefault: number;
  level: number;
  parentId: number;
  serviceIdentifier?: {
    name: string;
  };
}

const LEVEL = 2

const ServicesTwo = React.memo(() => {
  const { oneId } = useParams()
  const [isActive, setIsActive] = useState(true)
  const serviceData = useSelector(serviceTwoSelector) as Service[]
  const status = useSelector(statusSelector)
  const dispatch = useDispatch<AppDispatch>()

  // Memoize the fetch calls
  const fetchData = useCallback(() => {
    dispatch(fetchServices({ level: 2, parentId: oneId }))
  }, [dispatch, oneId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [sort, setSort] = useState(0)
  const [prioritySort, setPrioritySort] = useState<0 | 1>(0)
  const [admin, setAdmin] = React.useState(true)
  const [notAdmin, setNotAdmin] = React.useState(false)
  const [both, setBoth] = React.useState(false)
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table')
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const { getTableData, Modals } = useServiceTableConfig(2);

  // Memoize the filter functions
  const checkFilter = useCallback(() => {
    if (admin && isActive) {
      return serviceData.filter((f: Service) => f.isAdmin === 1 && f.isActive === 1)
    } else if (admin && !isActive) {
      return serviceData.filter((f: Service) => f.isAdmin === 1 && f.isActive === 0)
    } else if (notAdmin && isActive) {
      return serviceData.filter((f: Service) => f.isAdmin !== 1 && f.isActive === 1)
    } else if (notAdmin && !isActive) {
      return serviceData.filter((f: Service) => f.isAdmin !== 1 && f.isActive === 0)
    } else if (both && isActive) {
      return serviceData.filter((f: Service) => f.isActive === 1)
    } else if (both && !isActive) {
      return serviceData.filter((f: Service) => f.isActive === 0)
    } else {
      return serviceData
    }
  }, [admin, isActive, notAdmin, both, serviceData]);

  // Memoize the sort function
  const sortData = useCallback((data: Service[], sortType: string) => {
    return data.sort((a, b) => a.name.localeCompare(b.name) * (sortType === 'asc' ? 1 : -1))
  }, []);

  const sortByPriority = useCallback((data: Service[], order: 'asc' | 'desc') => {
    const factor = order === 'asc' ? 1 : -1
    return data.sort((a, b) => {
      const pa = typeof (a as any)?.priority === 'number' ? (a as any).priority : Number((a as any)?.priority)
      const pb = typeof (b as any)?.priority === 'number' ? (b as any).priority : Number((b as any)?.priority)
      const aValid = Number.isFinite(pa) && pa > 0
      const bValid = Number.isFinite(pb) && pb > 0
      if (aValid && bValid) return (pa - pb) * factor
      if (aValid && !bValid) return -1
      if (!aValid && bValid) return 1
      return 0
    })
  }, []);

  // Memoize the sorted data
  const SortedServiceData = useMemo(() => {
    let data = checkFilter();
    if (prioritySort === 1) {
      data = sortByPriority([...data], 'asc')
    } else {
      switch (sort) {
        case 1:
          data = sortData([...data], 'asc')
          break
        case 2:
          data = sortData([...data], 'desc')
          break
        default:
          data = data
      }
    }
    return data
  }, [sort, prioritySort, checkFilter, sortData, sortByPriority]);

  // Memoize the filtered data
  const filteredData = useMemo(() =>
    SortedServiceData?.filter((ser: Service) => ser.parentId === Number(oneId)),
    [SortedServiceData, oneId]
  );

  // Memoize the table data
  const tableData = useMemo(() =>
    getTableData(filteredData, currentPage, entriesPerPage),
    [filteredData, currentPage, entriesPerPage, getTableData]
  );

  // Memoize the handlers
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newPerPage: number) => {
    setEntriesPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const toggleSort = useCallback(() => {
    setSort((prevSort) => (prevSort + 1) % 3)
  }, []);

  const togglePrioritySort = useCallback(() => {
    setPrioritySort((prev) => (prev === 0 ? 1 : 0))
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(viewMode === 'card' ? 'table' : 'card')
  }, [viewMode]);


  return (
    <ServicesLayout>
      <div className='d-flex justify-content-end mt-n8 mb-3 dropdown'>
        <button
          className='btn btn-sm btn-color-white border-0 me-3 bg-primary d-flex align-items-center gap-2 px-3 py-2'
          onClick={() => setViewMode(viewMode === 'card' ? 'table' : 'card')}
        >
          {viewMode === 'card' ? (
            <>
              <BsGrid3X3GapFill size={20} />

            </>
          ) : (
            <>
              <BsListUl size={21} />

            </>
          )}
        </button>
        <div
          onClick={toggleSort}
          className={`btn btn-sm btn-icon btn-color-white border-0 me-2 ${sort ? 'bg-primary' : 'bg-secondary'}`}
        >
          <img
            style={{ filter: 'brightness(200 %) saturate(0%)' }}
            src={`${sort == 2 ? '/media/ztoa.png' : '/media/atoz.png'}`}
            alt='icon'
            className='my-1'
          />
        </div>
        <div
          onClick={togglePrioritySort}
          className={`btn btn-sm btn-icon btn-color-white border-0 me-2 ${prioritySort ? 'bg-primary' : 'bg-secondary text-black'}`}
          title='Sort by Priority'
        >
          <span className='fw-bold'>P</span>
        </div>
        <div
          style={{ cursor: 'auto' }}
          className='btn btn-sm btn-icon btn-color-white border-0 me-2 bg-primary'
        >
          {filteredData?.length}
        </div>
        <button
          type='button'
          className='btn btn-sm btn-icon btn-color-white btn-active-color-white border-0 me-n3 bg-primary'
          id='dropdownMenuButton2'
          data-bs-toggle='dropdown'
          aria-expanded='false'
        >
          <KTSVG path='/media/icons/duotune/general/gen024.svg' className='svg-icon-2' />
        </button>
        <ul
          className='dropdown-menu dropdown-menu-light w-250px w-md-300px'
          aria-labelledby='dropdownMenuButton2'
        >
          <div className='px-7 py-5'>
            <div className='fs-5 text-dark fw-bolder'>Filter Options</div>
          </div>

          <div className='separator border-gray-200'></div>

          <div className='px-7 pt-5'>
            <div className='mb-10'>
              <label className='form-label fw-bold mb-4'>Services created by:</label>

              <div className='d-flex'>
                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                  <input
                    className='form-check-input'
                    type='radio'
                    onChange={(e) => {
                      setNotAdmin(true)
                      setAdmin(false)
                      setBoth(false)
                    }}
                    checked={notAdmin ? true : false}
                  />
                  <span className='form-check-label'>Pro</span>
                </label>

                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                  <input
                    className='form-check-input'
                    type='radio'
                    onChange={() => {
                      setAdmin(true)
                      setNotAdmin(false)
                      setBoth(false)
                    }}
                    checked={admin ? true : false}
                  />
                  <span className='form-check-label'>Admin</span>
                </label>
                <label className='form-check form-check-sm form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    onChange={() => {
                      setBoth(true)
                      setAdmin(false)
                      setNotAdmin(false)
                    }}
                    checked={both ? true : false}
                  />
                  <span className='form-check-label'>Both</span>
                </label>
              </div>
              <label className='form-label fw-bold mt-6'>Services status:</label>

              <div className='d-flex'>
                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                  <input
                    className='form-check-input'
                    type='radio'
                    onChange={(e) => {
                      setIsActive(true)
                    }}
                    checked={isActive ? true : false}
                  />
                  <span className='form-check-label'>Active</span>
                </label>

                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                  <input
                    className='form-check-input'
                    type='radio'
                    onChange={(e) => {
                      setIsActive(false)
                    }}
                    checked={!isActive ? true : false}
                  />
                  <span className='form-check-label'>InActive</span>
                </label>
              </div>
            </div>
          </div>
        </ul>
      </div>
      <div className='row mt-5'>
        {status === 'loading' ? (
          viewMode === 'card' ? (
            <ServiceLoading />
          ) : (
            <TableLoading />
          )
        ) : viewMode === 'card' ? (
          filteredData?.map(
            (ser: Service, idx: number) => <ServiceCard key={idx} data={ser} />
          )
        ) : (
          <div className='card-body'>
            <MUITable
              data={tableData}
              page={currentPage}
              perPage={entriesPerPage}
              setPage={handlePageChange}
              setPerPage={handleRowsPerPageChange}
            />
          </div>
        )}
      </div>
      <Modals />
    </ServicesLayout>
  )
})

export default ServicesTwo
